// src/components/templates/diary/DiaryNotesView.jsx
import React, { useState, useMemo } from 'react';
import { 
  StickyNote, Lightbulb, Info, RefreshCw, Calendar, ArrowRight, Search,
  Filter, Grid3X3, List, Trash2, Copy, ExternalLink, Clock, Tag,
  ChevronDown, ChevronUp, MoreVertical, CheckSquare, MessageSquare,
  TrendingUp, Bookmark, BookmarkCheck
} from 'lucide-react';
import { Card, Badge, Button, MeetingStatus, MinuteType } from './UIComponents';

// Note categories
const NoteCategory = {
  GENERAL: 'GENERAL',
  IDEA: 'IDEA',
  UPDATE: 'UPDATE',
  TASK_UPDATE: 'TASK_UPDATE'
};

// --- NOTE CARD COMPONENT ---
const NoteCard = ({ note, onSelectMeeting, onDelete, onCopy, onBookmark, viewMode }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const getCategoryColor = (cat) => {
    switch (cat) {
      case NoteCategory.IDEA: return 'bg-yellow-50 text-yellow-800 border-yellow-200 hover:bg-yellow-100';
      case NoteCategory.UPDATE: return 'bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100';
      case NoteCategory.TASK_UPDATE: return 'bg-green-50 text-green-800 border-green-200 hover:bg-green-100';
      default: return 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50';
    }
  };

  const getCategoryIcon = (cat) => {
    switch (cat) {
      case NoteCategory.IDEA: return <Lightbulb className="w-4 h-4" />;
      case NoteCategory.UPDATE: return <Info className="w-4 h-4" />;
      case NoteCategory.TASK_UPDATE: return <RefreshCw className="w-4 h-4" />;
      default: return <StickyNote className="w-4 h-4" />;
    }
  };

  const getCategoryGradient = (cat) => {
    switch (cat) {
      case NoteCategory.IDEA: return 'from-yellow-400 to-amber-500';
      case NoteCategory.UPDATE: return 'from-blue-400 to-indigo-500';
      case NoteCategory.TASK_UPDATE: return 'from-green-400 to-emerald-500';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  const isLongContent = note.content.length > 200;
  const displayContent = isLongContent && !isExpanded 
    ? note.content.slice(0, 200) + '...' 
    : note.content;

  if (viewMode === 'list') {
    return (
      <div className={`p-4 rounded-xl border shadow-sm transition-all ${getCategoryColor(note.category)} flex items-start gap-4`}>
        {/* Category Icon */}
        <div className={`p-2 rounded-lg bg-gradient-to-br ${getCategoryGradient(note.category)} text-white shrink-0`}>
          {getCategoryIcon(note.category)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm whitespace-pre-wrap leading-relaxed flex-1">
              {displayContent}
            </p>
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-[10px] text-gray-400">{note.timestamp}</span>
              {note.isBookmarked && <Bookmark className="w-3 h-3 text-amber-500 fill-amber-500" />}
            </div>
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />{note.meetingDate}</span>
              <span className="truncate max-w-[150px]">{note.meetingTitle}</span>
            </div>
            <button 
              onClick={() => onSelectMeeting?.(note.originalMeeting)}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center"
            >
              Open <ArrowRight className="w-3 h-3 ml-1" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Grid/Masonry view (default)
  return (
    <div className="break-inside-avoid">
      <div className={`p-5 rounded-xl border shadow-sm transition-all hover:shadow-lg ${getCategoryColor(note.category)} relative group`}>
        
        {/* Bookmark indicator */}
        {note.isBookmarked && (
          <div className="absolute -top-1 -right-1">
            <Bookmark className="w-5 h-5 text-amber-500 fill-amber-500 drop-shadow" />
          </div>
        )}

        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center space-x-2">
            <div className={`p-1.5 rounded-lg bg-gradient-to-br ${getCategoryGradient(note.category)} text-white shadow-sm`}>
              {getCategoryIcon(note.category)}
            </div>
            <span className="text-xs font-bold uppercase tracking-wider opacity-70">
              {(note.category || 'GENERAL').replace('_', ' ')}
            </span>
          </div>
          
          {/* Actions Menu */}
          <div className="relative">
            <button 
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-white/50 transition-all"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-20">
                  {onBookmark && (
                    <button 
                      onClick={() => { onBookmark(note); setShowMenu(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      {note.isBookmarked ? <BookmarkCheck className="w-4 h-4 mr-2" /> : <Bookmark className="w-4 h-4 mr-2" />}
                      {note.isBookmarked ? 'Unbookmark' : 'Bookmark'}
                    </button>
                  )}
                  {onCopy && (
                    <button 
                      onClick={() => { onCopy(note.content); setShowMenu(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <Copy className="w-4 h-4 mr-2" /> Copy
                    </button>
                  )}
                  {onDelete && (
                    <button 
                      onClick={() => { onDelete(note); setShowMenu(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Timestamp */}
        <div className="flex items-center gap-2 mb-3 text-[10px] opacity-60">
          <Clock className="w-3 h-3" />
          <span>{note.timestamp}</span>
          <span>•</span>
          <span>{note.meetingDate}</span>
        </div>

        {/* Content */}
        <p className="text-sm whitespace-pre-wrap leading-relaxed mb-4 font-normal">
          {displayContent}
        </p>

        {/* Expand/Collapse for long content */}
        {isLongContent && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs font-medium opacity-70 hover:opacity-100 flex items-center mb-3"
          >
            {isExpanded ? (
              <><ChevronUp className="w-3 h-3 mr-1" /> Show less</>
            ) : (
              <><ChevronDown className="w-3 h-3 mr-1" /> Read more</>
            )}
          </button>
        )}

        {/* Footer / Link to Meeting */}
        <div className="pt-3 border-t border-black/10 flex items-center justify-between">
          <div className="flex items-center text-xs opacity-70 truncate max-w-[60%]">
            <MessageSquare className="w-3 h-3 mr-1.5 shrink-0" />
            <span className="truncate">{note.meetingTitle}</span>
          </div>
           
          <button 
            onClick={() => onSelectMeeting?.(note.originalMeeting)}
            className="flex items-center text-xs font-semibold hover:underline opacity-80 hover:opacity-100 transition-opacity"
          >
            Open Meeting <ExternalLink className="w-3 h-3 ml-1" />
          </button>
        </div>

      </div>
    </div>
  );
};

// --- STATS BAR ---
const StatsBar = ({ stats }) => (
  <div className="flex flex-wrap gap-4 text-sm">
    <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
      <StickyNote className="w-4 h-4 text-gray-500" />
      <span className="font-medium">{stats.total}</span>
      <span className="text-gray-500">Total</span>
    </div>
    <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-100 rounded-full text-yellow-700">
      <Lightbulb className="w-4 h-4" />
      <span className="font-medium">{stats.ideas}</span>
      <span>Ideas</span>
    </div>
    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-full text-blue-700">
      <Info className="w-4 h-4" />
      <span className="font-medium">{stats.updates}</span>
      <span>Updates</span>
    </div>
    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-full text-green-700">
      <RefreshCw className="w-4 h-4" />
      <span className="font-medium">{stats.taskUpdates}</span>
      <span>Task Updates</span>
    </div>
  </div>
);

// --- MAIN COMPONENT ---
export const DiaryNotesView = ({ 
  meetings = [], 
  onSelectMeeting,
  onDeleteNote,
  onBookmarkNote 
}) => {
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest', 'meeting'
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  // Flatten all notes from all meetings
  const allNotes = useMemo(() => {
    return meetings.flatMap(meeting => 
      (meeting.meetingNotes || []).map(note => ({
        ...note,
        meetingTitle: meeting.title,
        meetingDate: meeting.date,
        meetingId: meeting.id,
        meetingStatus: meeting.status,
        originalMeeting: meeting
      }))
    );
  }, [meetings]);

  // Stats
  const stats = useMemo(() => ({
    total: allNotes.length,
    ideas: allNotes.filter(n => n.category === NoteCategory.IDEA).length,
    updates: allNotes.filter(n => n.category === NoteCategory.UPDATE).length,
    taskUpdates: allNotes.filter(n => n.category === NoteCategory.TASK_UPDATE).length,
    general: allNotes.filter(n => !n.category || n.category === NoteCategory.GENERAL).length,
    bookmarked: allNotes.filter(n => n.isBookmarked).length,
  }), [allNotes]);

  // Filter and sort
  const filteredNotes = useMemo(() => {
    return allNotes
      .filter(note => {
        // Category filter
        const matchesCategory = filterCategory === 'ALL' || note.category === filterCategory;
        
        // Search filter
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          note.content.toLowerCase().includes(query) ||
          note.meetingTitle.toLowerCase().includes(query);
        
        // Bookmark filter
        const matchesBookmark = !showBookmarkedOnly || note.isBookmarked;
        
        return matchesCategory && matchesSearch && matchesBookmark;
      })
      .sort((a, b) => {
        if (sortOrder === 'newest') {
          const dateA = new Date(a.meetingDate).getTime();
          const dateB = new Date(b.meetingDate).getTime();
          if (dateA !== dateB) return dateB - dateA;
          return (b.timestamp || '').localeCompare(a.timestamp || '');
        }
        if (sortOrder === 'oldest') {
          const dateA = new Date(a.meetingDate).getTime();
          const dateB = new Date(b.meetingDate).getTime();
          if (dateA !== dateB) return dateA - dateB;
          return (a.timestamp || '').localeCompare(b.timestamp || '');
        }
        if (sortOrder === 'meeting') {
          return a.meetingTitle.localeCompare(b.meetingTitle);
        }
        return 0;
      });
  }, [allNotes, filterCategory, searchQuery, sortOrder, showBookmarkedOnly]);

  // Copy to clipboard
  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    // Could add toast notification here
  };

  const filterButtons = [
    { key: 'ALL', label: 'All Notes', count: stats.total, icon: StickyNote },
    { key: NoteCategory.GENERAL, label: 'General', count: stats.general, icon: StickyNote },
    { key: NoteCategory.IDEA, label: 'Ideas', count: stats.ideas, icon: Lightbulb },
    { key: NoteCategory.UPDATE, label: 'Updates', count: stats.updates, icon: Info },
    { key: NoteCategory.TASK_UPDATE, label: 'Task Updates', count: stats.taskUpdates, icon: RefreshCw },
  ];

  return (
    <div className="flex flex-col h-full bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <StickyNote className="w-6 h-6 mr-2 text-indigo-600" />
              All Notes
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {stats.total} notes from {meetings.length} meetings
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input 
                type="text" 
                placeholder="Search notes..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-64 pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
            </div>

            {/* View Toggle */}
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2.5 ${viewMode === 'grid' ? 'bg-indigo-50 text-indigo-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                title="Grid view"
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2.5 ${viewMode === 'list' ? 'bg-indigo-50 text-indigo-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="mt-4">
          <StatsBar stats={stats} />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="px-6 py-3 bg-white border-b border-gray-200 shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Category Filters */}
          <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
            {filterButtons.map(btn => (
              <button
                key={btn.key}
                onClick={() => setFilterCategory(btn.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap flex items-center gap-1.5 ${
                  filterCategory === btn.key 
                    ? 'bg-gray-900 text-white border-gray-900' 
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <btn.icon className="w-3 h-3" />
                {btn.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  filterCategory === btn.key ? 'bg-white/20' : 'bg-gray-100'
                }`}>
                  {btn.count}
                </span>
              </button>
            ))}
          </div>

          {/* Sort & Bookmark Filter */}
          <div className="flex items-center gap-2">
            {stats.bookmarked > 0 && (
              <button
                onClick={() => setShowBookmarkedOnly(!showBookmarkedOnly)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                  showBookmarkedOnly 
                    ? 'bg-amber-500 text-white border-amber-500' 
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Bookmark className={`w-3 h-3 ${showBookmarkedOnly ? 'fill-white' : ''}`} />
                Bookmarked ({stats.bookmarked})
              </button>
            )}

            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="meeting">By Meeting</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grid Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto">
          {filteredNotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                {searchQuery || filterCategory !== 'ALL' || showBookmarkedOnly ? (
                  <Search className="w-10 h-10 text-gray-300" />
                ) : (
                  <StickyNote className="w-10 h-10 text-gray-300" />
                )}
              </div>
              <p className="text-lg font-medium text-gray-700">
                {searchQuery || filterCategory !== 'ALL' || showBookmarkedOnly 
                  ? 'No matching notes' 
                  : 'No notes yet'
                }
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {searchQuery || filterCategory !== 'ALL' || showBookmarkedOnly 
                  ? 'Try adjusting your filters'
                  : 'Add notes within your meetings to see them here'
                }
              </p>
              {(searchQuery || filterCategory !== 'ALL' || showBookmarkedOnly) && (
                <button
                  onClick={() => { setSearchQuery(''); setFilterCategory('ALL'); setShowBookmarkedOnly(false); }}
                  className="mt-4 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Results count */}
              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <span>Showing {filteredNotes.length} of {allNotes.length} notes</span>
              </div>

              {/* Notes Grid/List */}
              <div className={viewMode === 'grid' 
                ? "columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6" 
                : "space-y-4"
              }>
                {filteredNotes.map((note, index) => (
                  <NoteCard 
                    key={`${note.id}-${index}`}
                    note={note}
                    viewMode={viewMode}
                    onSelectMeeting={onSelectMeeting}
                    onDelete={onDeleteNote}
                    onCopy={handleCopy}
                    onBookmark={onBookmarkNote}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiaryNotesView;